/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption, IDropdownProps, IDropdownStyleProps, IDropdownStyles } from '@fluentui/react/lib/Dropdown';
import { Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton, CalloutButtonProps } from 'src/core/components/CalloutButton';
import { LuisDropdown, LuisDropdownProps } from 'src/core/components/LuisDropdown';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { CultureStore } from 'src/core/stores/CultureStore';
import { labelDescriptionIconClassName } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';

export type SummarizationSentenceProps = Partial<LuisDropdownProps> & {
    disabled?: boolean;
    descriptionMessage?: string;
    selectedNumberOfSentencesKey: number;
    onNumberOfSentencesChange: (optionKey: number) => void;
    numberOfSentencesList: Pick<CultureStore, 'id' | 'name'>[];
};

const messages = defineMessages({
    selectNumberOfSentencesLabel: {
        id: 'languageStudio.summarizationDemoPage.selectNumberOfSentencesLabel',
        defaultMessage: 'Enter number of sentences in summary (max 20)'
    },
    selectNumberOfSentencesPlaceholder: {
        id: 'languageStudio.summarizationDemoPage.selectNumberOfSentencesPlaceholder',
        defaultMessage: 'Select a number of sentences'
    },
    selectNumberOfSentencesDescription: {
        id: 'languageStudio.summarizationDemoPage.selectNumberOfSentencesDescription',
        defaultMessage:
            'This is the number of sentences your summary\n        will contain. If the number of sentences chosen\n        here is greater than the number of sentences\n        in the input text, the output summary will just\n        be the original text.'
    }
});

const languageDropdownStyles = classNamesFunction<IDropdownStyleProps, IDropdownStyles>()({
    dropdown: { minWidth: '300px' },
    root: { alignSelf: 'flex-start' }
});

const renderNotRequiredLabel = ({
    labelProps,
    descriptionIconProps,
    labelDefaultRenderer
}: {
    labelProps: IDropdownProps;
    labelDefaultRenderer: (props?: IDropdownProps) => JSX.Element;
    descriptionIconProps: CalloutButtonProps;
}) => (
    <Stack horizontal verticalAlign="center" tokens={{ padding: '2px 0' }}>
        {labelDefaultRenderer(labelProps)}
        <CalloutButton {...descriptionIconProps} iconClassName={labelDescriptionIconClassName} />
    </Stack>
);

export const SummarizationSentence = (props: SummarizationSentenceProps) => {
    const { localize } = useLocalization();
    const {
        disabled,
        onNumberOfSentencesChange,
        numberOfSentencesList = [],
        selectedNumberOfSentencesKey,
        descriptionMessage = localize(messages.selectNumberOfSentencesDescription),
        ...rest
    } = props;

    let sentencesOptions: IDropdownOption[] = [];
    sentencesOptions = [...numberOfSentencesList.map(n => ({ key: n.id, text: n.name }))];

    return (
        <LuisDropdown
            orderByKey="none"
            disabled={disabled}
            options={sentencesOptions}
            data-automation-id="numberOfSentencesDropdown"
            selectedKey={String(selectedNumberOfSentencesKey)}
            label={localize(messages.selectNumberOfSentencesLabel)}
            placeholder={localize(messages.selectNumberOfSentencesPlaceholder)}
            ariaLabel={localize(messages.selectNumberOfSentencesPlaceholder)}
            onChange={(_, option) => onNumberOfSentencesChange(option.key as number)}
            calloutProps={{ styles: { calloutMain: { maxHeight: '300px !important' } } }}
            onRenderLabel={(labelProps, labelDefaultRenderer) =>
                renderNotRequiredLabel({
                    labelProps,
                    labelDefaultRenderer,
                    descriptionIconProps: { isDarkMode: false, content: descriptionMessage }
                })
            }
            {...rest}
            styles={mergeStyleSets(languageDropdownStyles, rest.styles)}
        />
    );
};
